import { EntityType } from '@frontend/common';
import { accountIdFromResolvedEntity } from '@frontend/components/common';
import { defaultCellClassName } from '@frontend/components/list';
import { ReactiveObjectList } from '@frontend/components/list-v2';
import { Application, Feature } from '@frontend/functionality/types';
import { CommonMessage } from '@frontend/lang';
import { LaundryTransactionClient } from '@frontend/laundry-app/api';
import { LaundryTransaction } from '@frontend/laundry-app/types';
import { TransactionStateBadge } from '@frontend/transaction/common';
import { TransactionState, TransactionStateName } from '@frontend/transaction/types';
import { FormattedMessage } from 'react-intl';

import { MultiEntityButton } from '../../../components/multi-entity-button/multi-entity-button.component';
import { renderCopyableTrackingNumberCell } from '../../../components/object-list-cells/copyable-tracking-number-cell.component';
import { usePlatformModalHost } from '../../../modal/platform-modal-host/platform-modal-host.context';
import { PlatformObjectBadge } from '../../../modal/platform-object-badge/platform-object-badge.component';
import OverviewHeader from '../../header/overview-header';
import { TabFilter } from '../../tab-filter/tab-filter.component';
import { useShipments } from './shipments.controller';

export const Shipments = () => {
    const viewProps = useShipments();

    return (
        <div
            key={'laundry-items-list'}
            className='size-lf-stretch bg-background-default inline-flex w-full flex-col items-start justify-start gap-6'>
            <OverviewHeader
                title={
                    <FormattedMessage
                        id='feature.shipments'
                        defaultMessage='Shipments'
                    />
                }
            />
            <div className='inline-flex flex-col items-start justify-start gap-4 self-stretch'>
                <TabFilter
                    keys={['incoming', 'outgoing', 'completed']}
                    displayMap={
                        new Map([
                            [
                                'incoming',
                                <FormattedMessage
                                    id='incoming'
                                    defaultMessage='Incoming'
                                    key='incoming'
                                />
                            ],
                            [
                                'outgoing',
                                <FormattedMessage
                                    id='outgoing'
                                    defaultMessage='Outgoing'
                                    key='outgoing'
                                />
                            ],
                            [
                                'completed',
                                <FormattedMessage
                                    id='completed'
                                    defaultMessage='Completed'
                                    key='completed'
                                />
                            ]
                        ])
                    }
                    onChange={viewProps.setActiveTab}
                />
                <ShipmentsList
                    key={viewProps.activeTab}
                    {...viewProps}
                />
            </div>
        </div>
    );
};

const ShipmentsList = (viewProps: ReturnType<typeof useShipments>) => {
    const { openPlatformDetail } = usePlatformModalHost();
    switch (viewProps.activeTab) {
        case 'incoming':
            return (
                <ReactiveObjectList<LaundryTransaction>
                    fetch={(arg) =>
                        LaundryTransactionClient.fetchIncomingDirtyLaundry(viewProps.currentAccount, {
                            ...arg,
                            state: TransactionState.NOT_COMPLETED_CUSTOMER.map((state) => state.value)
                        })
                    }
                    useStaticListId
                    onSelectElement={(obj) =>
                        obj.application &&
                        obj.feature &&
                        openPlatformDetail({ entityType: EntityType.TRANSACTION, id: obj.id, context: { application: obj.application } })
                    }
                    id='laundry-incoming-shipments'
                    dispatch={viewProps.dispatch}
                    activeFilter={{
                        searchKeys: ['tracking_number', 'sender'],
                        filterKeys: ['senders', 'state', 'before', 'after'],
                        displayName: new Map([
                            ['tracking_number', CommonMessage.OBJECTS.TRANSACTION.SHIPPING_LABEL],
                            ['senders', CommonMessage.OBJECTS.TRANSACTION.SENDER],
                            ['content', CommonMessage.OBJECTS.TRANSACTION.CONTENT],
                            ['state', CommonMessage.OBJECTS.COMMON_FIELDS.STATUS],
                            ['before', CommonMessage.OBJECTS.COMMON_FIELDS.BEFORE],
                            ['after', CommonMessage.OBJECTS.COMMON_FIELDS.AFTER]
                        ]),
                        queryKeyMap: new Map([['senders', 'sender_id']])
                    }}
                    columns={{
                        columns: ['tracking_number', 'senders', 'content', 'state', 'creation_timestamp'],
                        headerOverwrite: new Map([
                            ['tracking_number', CommonMessage.OBJECTS.TRANSACTION.SHIPPING_LABEL],
                            ['senders', CommonMessage.OBJECTS.TRANSACTION.SENDER],
                            ['content', CommonMessage.OBJECTS.TRANSACTION.CONTENT],
                            ['state', CommonMessage.OBJECTS.COMMON_FIELDS.STATUS],
                            ['creation_timestamp', CommonMessage.OBJECTS.COMMON_FIELDS.CREATED_AT]
                        ]),
                        customCells: new Map([
                            ['tracking_number', renderCopyableTrackingNumberCell],
                            [
                                'content',
                                (obj) => {
                                    if (obj.row.original.contents === null || obj.row.original.contents.length === 0) {
                                        return (
                                            <td className={defaultCellClassName}>
                                                <div className='text-foreground-default justify-start text-sm font-medium'>/</div>
                                            </td>
                                        );
                                    }
                                    const result: Record<string, number> = {};
                                    obj.row.original.contents.forEach((content) => {
                                        if (!result[content.product_name]) {
                                            result[content.product_name] = 1;
                                        } else {
                                            result[content.product_name]++;
                                        }
                                    });

                                    return (
                                        <td
                                            key={obj.row.original.contents[0].id}
                                            className={defaultCellClassName}>
                                            <div className='inline-flex items-center justify-start gap-3'>
                                                {Object.entries(result)
                                                    .slice(0, 4)
                                                    .map(([key, value]) => (
                                                        <span key={key}>
                                                            {value}x {key}
                                                        </span>
                                                    ))}
                                                {Object.entries(result).length > 4 && (
                                                    <MultiEntityButton
                                                        entities={Object.entries(result).slice(4)}
                                                        render={(obj) => (
                                                            <span>
                                                                {obj[1]}x {obj[0]}
                                                            </span>
                                                        )}
                                                    />
                                                )}
                                            </div>
                                        </td>
                                    );
                                }
                            ],
                            [
                                'state',
                                (obj) => {
                                    return (
                                        <td className={defaultCellClassName}>
                                            <TransactionStateBadge state={obj.row.original.state} />
                                        </td>
                                    );
                                }
                            ],
                            [
                                'senders',
                                (obj) => {
                                    if (obj.row.original.senders === null || obj.row.original.senders.length === 0) {
                                        return (
                                            <td className={defaultCellClassName}>
                                                <div className='text-foreground-default justify-start text-sm font-medium'>/</div>
                                            </td>
                                        );
                                    }
                                    return (
                                        <td
                                            key={obj.row.original.senders[0].entity_id}
                                            className={defaultCellClassName}>
                                            <div className='flex gap-2.5 truncate text-sm font-medium'>
                                                <PlatformObjectBadge
                                                    id={obj.row.original.senders[0].entity_id}
                                                    entityType={EntityType.USER}
                                                    display={obj.row.original.senders[0].name}
                                                    key={obj.row.original.senders[0].entity_id}
                                                    foreign={obj.row.original.senders[0].foreign}
                                                    accountId={accountIdFromResolvedEntity(obj.row.original.senders[0])}
                                                />
                                                {obj.row.original.senders.length > 1 && (
                                                    <MultiEntityButton
                                                        entities={obj.row.original.senders.slice(1)}
                                                        render={(obj) => (
                                                            <PlatformObjectBadge
                                                                id={obj.entity_id}
                                                                entityType={EntityType.USER}
                                                                display={obj.name}
                                                                key={obj.entity_id}
                                                                foreign={obj.foreign}
                                                                accountId={accountIdFromResolvedEntity(obj)}
                                                            />
                                                        )}
                                                    />
                                                )}
                                            </div>
                                        </td>
                                    );
                                }
                            ]
                        ])
                    }}
                    type={EntityType.TRANSACTION}
                />
            );
        case 'outgoing':
            return (
                <ReactiveObjectList<LaundryTransaction>
                    fetch={(arg) =>
                        LaundryTransactionClient.fetchOutgoingCleanLaundry(viewProps.currentAccount, {
                            ...arg,
                            state: TransactionState.NOT_COMPLETED_CUSTOMER.map((state) => state.value)
                        })
                    }
                    useStaticListId
                    onSelectElement={(obj) =>
                        openPlatformDetail({ entityType: EntityType.TRANSACTION, id: obj.id, context: { application: Application.LAUNDRY } })
                    }
                    id='laundry-outgoing-shipments'
                    dispatch={viewProps.dispatch}
                    activeFilter={{
                        searchKeys: ['tracking_number', 'receiver'],
                        filterKeys: ['receivers', 'state', 'before', 'after'],
                        displayName: new Map([
                            ['tracking_number', CommonMessage.OBJECTS.TRANSACTION.SHIPPING_LABEL],
                            ['receivers', CommonMessage.OBJECTS.TRANSACTION.RECEIVER],
                            ['content', CommonMessage.OBJECTS.TRANSACTION.CONTENT],
                            ['state', CommonMessage.OBJECTS.COMMON_FIELDS.STATUS],
                            ['before', CommonMessage.OBJECTS.COMMON_FIELDS.BEFORE],
                            ['after', CommonMessage.OBJECTS.COMMON_FIELDS.AFTER]
                        ]),
                        queryKeyMap: new Map([['receivers', 'receiver_id']])
                    }}
                    columns={{
                        columns: ['tracking_number', 'receivers', 'content', 'state', 'creation_timestamp'],
                        headerOverwrite: new Map([
                            ['tracking_number', CommonMessage.OBJECTS.TRANSACTION.SHIPPING_LABEL],
                            ['receivers', CommonMessage.OBJECTS.TRANSACTION.RECEIVER],
                            ['content', CommonMessage.OBJECTS.TRANSACTION.CONTENT],
                            ['state', CommonMessage.OBJECTS.COMMON_FIELDS.STATUS],
                            ['creation_timestamp', CommonMessage.OBJECTS.COMMON_FIELDS.CREATED_AT]
                        ]),
                        customCells: new Map([
                            ['tracking_number', renderCopyableTrackingNumberCell],
                            [
                                'content',
                                (obj) => {
                                    if (obj.row.original.contents === null || obj.row.original.contents.length === 0) {
                                        return (
                                            <td className={defaultCellClassName}>
                                                <div className='text-foreground-default justify-start text-sm font-medium'>/</div>
                                            </td>
                                        );
                                    }
                                    const result: Record<string, number> = {};
                                    obj.row.original.contents.forEach((content) => {
                                        if (!result[content.product_name]) {
                                            result[content.product_name] = 1;
                                        } else {
                                            result[content.product_name]++;
                                        }
                                    });

                                    return (
                                        <td
                                            key={obj.row.original.contents[0].id}
                                            className={defaultCellClassName}>
                                            <div className='inline-flex items-center justify-start gap-3'>
                                                {Object.entries(result)
                                                    .slice(0, 4)
                                                    .map(([key, value]) => (
                                                        <span key={key}>
                                                            {value}x {key}
                                                        </span>
                                                    ))}
                                                {Object.entries(result).length > 4 && (
                                                    <MultiEntityButton
                                                        entities={Object.entries(result).slice(4)}
                                                        render={(obj) => (
                                                            <span>
                                                                {obj[1]}x {obj[0]}
                                                            </span>
                                                        )}
                                                    />
                                                )}
                                            </div>
                                        </td>
                                    );
                                }
                            ],
                            [
                                'state',
                                (obj) => {
                                    return (
                                        <td className={defaultCellClassName}>
                                            <TransactionStateBadge state={obj.row.original.state} />
                                        </td>
                                    );
                                }
                            ],
                            [
                                'receivers',
                                (obj) => {
                                    if (obj.row.original.receivers === null || obj.row.original.receivers.length === 0) {
                                        return (
                                            <td className={defaultCellClassName}>
                                                <div className='text-foreground-default justify-start text-sm font-medium'>/</div>
                                            </td>
                                        );
                                    }
                                    return (
                                        <td
                                            key={obj.row.original.receivers[0].entity_id}
                                            className={defaultCellClassName}>
                                            <div className='flex gap-2.5 truncate text-sm font-medium'>
                                                <PlatformObjectBadge
                                                    id={obj.row.original.receivers[0].entity_id}
                                                    entityType={EntityType.USER}
                                                    display={obj.row.original.receivers[0].name}
                                                    key={obj.row.original.receivers[0].entity_id}
                                                    foreign={obj.row.original.receivers[0].foreign}
                                                    accountId={accountIdFromResolvedEntity(obj.row.original.receivers[0])}
                                                />
                                                {obj.row.original.receivers.length > 1 && (
                                                    <MultiEntityButton
                                                        entities={obj.row.original.receivers.slice(1)}
                                                        render={(obj) => (
                                                            <PlatformObjectBadge
                                                                id={obj.entity_id}
                                                                entityType={EntityType.USER}
                                                                display={obj.name}
                                                                key={obj.entity_id}
                                                                foreign={obj.foreign}
                                                                accountId={accountIdFromResolvedEntity(obj)}
                                                            />
                                                        )}
                                                    />
                                                )}
                                            </div>
                                        </td>
                                    );
                                }
                            ]
                        ])
                    }}
                    type={EntityType.TRANSACTION}
                />
            );
        case 'completed':
            return (
                <ReactiveObjectList<LaundryTransaction>
                    fetch={(arg) =>
                        LaundryTransactionClient.fetchLaundryTransactions(viewProps.currentAccount, {
                            ...arg,
                            application: Application.LAUNDRY,
                            state: [
                                TransactionStateName.TRANSACTION_COMPLETED,
                                TransactionStateName.TRANSACTION_COMPLETED_WITH_FAILURES,
                                TransactionStateName.TRANSACTION_CANCELLED,
                                TransactionStateName.TRANSACTION_FAILED
                            ]
                        })
                    }
                    useStaticListId
                    onSelectElement={(obj) =>
                        openPlatformDetail({ entityType: EntityType.TRANSACTION, id: obj.id, context: { application: Application.LAUNDRY } })
                    }
                    id='laundry-completed-shipments'
                    dispatch={viewProps.dispatch}
                    activeFilter={{
                        searchKeys: ['tracking_number', 'sender', 'receiver'],
                        filterKeys: ['receivers', 'state'],
                        displayName: new Map([
                            ['tracking_number', CommonMessage.OBJECTS.TRANSACTION.SHIPPING_LABEL],
                            ['receivers', CommonMessage.OBJECTS.TRANSACTION.RECEIVER],
                            ['content', CommonMessage.OBJECTS.TRANSACTION.CONTENT],
                            ['state', CommonMessage.OBJECTS.COMMON_FIELDS.STATUS]
                        ]),
                        queryKeyMap: new Map([['receivers', 'receiver_id']])
                    }}
                    columns={{
                        columns: ['tracking_number', 'senders', 'receivers', 'content', 'status', 'update_timestamp'],
                        headerOverwrite: new Map([
                            ['tracking_number', CommonMessage.OBJECTS.TRANSACTION.SHIPPING_LABEL],
                            ['senders', CommonMessage.OBJECTS.TRANSACTION.SENDER],
                            ['receivers', CommonMessage.OBJECTS.TRANSACTION.RECEIVER],
                            ['content', CommonMessage.OBJECTS.TRANSACTION.CONTENT],
                            ['state', CommonMessage.OBJECTS.COMMON_FIELDS.STATUS],
                            ['update_timestamp', CommonMessage.OBJECTS.COMMON_FIELDS.UPDATED_AT]
                        ]),
                        customCells: new Map([
                            ['tracking_number', renderCopyableTrackingNumberCell],
                            [
                                'content',
                                (obj) => {
                                    if (obj.row.original.contents === null || obj.row.original.contents.length === 0) {
                                        return (
                                            <td className={defaultCellClassName}>
                                                <div className='text-foreground-default justify-start text-sm font-medium'>/</div>
                                            </td>
                                        );
                                    }
                                    const result: Record<string, number> = {};
                                    obj.row.original.contents.forEach((content) => {
                                        if (!result[content.product_name]) {
                                            result[content.product_name] = 1;
                                        } else {
                                            result[content.product_name]++;
                                        }
                                    });

                                    return (
                                        <td
                                            key={obj.row.original.contents[0].id}
                                            className={defaultCellClassName}>
                                            <div className='inline-flex items-center justify-start gap-3'>
                                                {Object.entries(result)
                                                    .slice(0, 4)
                                                    .map(([key, value]) => (
                                                        <span>
                                                            {value}x {key}
                                                        </span>
                                                    ))}
                                                {Object.entries(result).length > 4 && (
                                                    <MultiEntityButton
                                                        entities={Object.entries(result).slice(4)}
                                                        render={(obj) => (
                                                            <span>
                                                                {obj[1]}x {obj[0]}
                                                            </span>
                                                        )}
                                                    />
                                                )}
                                            </div>
                                        </td>
                                    );
                                }
                            ],
                            [
                                'status',
                                (obj) => {
                                    return (
                                        <td className={defaultCellClassName}>
                                            <TransactionStateBadge state={obj.row.original.state} />
                                        </td>
                                    );
                                }
                            ],
                            [
                                'senders',
                                (obj) => {
                                    if (obj.row.original.senders === null || obj.row.original.senders.length === 0) {
                                        return (
                                            <td className={defaultCellClassName}>
                                                <div className='text-foreground-default justify-start text-sm font-medium'>/</div>
                                            </td>
                                        );
                                    }
                                    return (
                                        <td
                                            key={obj.row.original.senders[0].entity_id}
                                            className={defaultCellClassName}>
                                            <div className='flex gap-2.5 truncate text-sm font-medium'>
                                                <PlatformObjectBadge
                                                    id={obj.row.original.senders[0].entity_id}
                                                    entityType={EntityType.USER}
                                                    display={obj.row.original.senders[0].name}
                                                    key={obj.row.original.senders[0].entity_id}
                                                    foreign={obj.row.original.senders[0].foreign}
                                                    accountId={accountIdFromResolvedEntity(obj.row.original.senders[0])}
                                                />
                                                {obj.row.original.senders.length > 1 && (
                                                    <MultiEntityButton
                                                        entities={obj.row.original.senders.slice(1)}
                                                        render={(obj) => (
                                                            <PlatformObjectBadge
                                                                id={obj.entity_id}
                                                                entityType={EntityType.USER}
                                                                display={obj.name}
                                                                key={obj.entity_id}
                                                                foreign={obj.foreign}
                                                                accountId={accountIdFromResolvedEntity(obj)}
                                                            />
                                                        )}
                                                    />
                                                )}
                                            </div>
                                        </td>
                                    );
                                }
                            ],
                            [
                                'receivers',
                                (obj) => {
                                    if (obj.row.original.receivers === null || obj.row.original.receivers.length === 0) {
                                        return (
                                            <td className={defaultCellClassName}>
                                                <div className='text-foreground-default justify-start text-sm font-medium'>/</div>
                                            </td>
                                        );
                                    }
                                    return (
                                        <td
                                            key={obj.row.original.receivers[0].entity_id}
                                            className={defaultCellClassName}>
                                            <div className='flex gap-2.5 truncate text-sm font-medium'>
                                                <PlatformObjectBadge
                                                    id={obj.row.original.receivers[0].entity_id}
                                                    entityType={EntityType.USER}
                                                    display={obj.row.original.receivers[0].name}
                                                    key={obj.row.original.receivers[0].entity_id}
                                                    foreign={obj.row.original.receivers[0].foreign}
                                                    accountId={accountIdFromResolvedEntity(obj.row.original.receivers[0])}
                                                />
                                                {obj.row.original.receivers.length > 1 && (
                                                    <MultiEntityButton
                                                        entities={obj.row.original.receivers.slice(1)}
                                                        render={(obj) => (
                                                            <PlatformObjectBadge
                                                                id={obj.entity_id}
                                                                entityType={EntityType.USER}
                                                                display={obj.name}
                                                                key={obj.entity_id}
                                                                foreign={obj.foreign}
                                                                accountId={accountIdFromResolvedEntity(obj)}
                                                            />
                                                        )}
                                                    />
                                                )}
                                            </div>
                                        </td>
                                    );
                                }
                            ]
                        ])
                    }}
                    type={EntityType.TRANSACTION}
                />
            );
        default:
            return null;
    }
};
